if (!customElements.get('variant-gallery')) {
  class VariantGallery extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('section-id');
      if (!this.sectionId) return;

      this.mediaIndex = this._readJSON(`MediaIndex-${this.sectionId}`);
      this.variantData = this._readJSON(`VariantGalleryData-${this.sectionId}`);

      // No-op when no variant galleries are configured
      if (!this.variantData || Object.keys(this.variantData).length === 0) return;

      this._tagItems();

      const initial = this._getSelectedVariant();
      if (initial) {
        this._applyFilter(String(initial.id), initial.featured_media?.id ?? null);
      }

      this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.sectionId) return;
        // Re-tag because product-info.js updateMedia may have re-ordered/replaced <li>s
        this._tagItems();
        this._applyFilter(String(data.variant.id), data.variant.featured_media?.id ?? null);
      });
    }

    disconnectedCallback() {
      this._unsubscribe?.();
    }

    _readJSON(id) {
      try {
        const el = document.getElementById(id);
        return el ? JSON.parse(el.textContent) : null;
      } catch {
        return null;
      }
    }

    _getSelectedVariant() {
      try {
        const productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
        const el = productInfo?.querySelector('[data-selected-variant]');
        return el ? JSON.parse(el.textContent) : null;
      } catch {
        return null;
      }
    }

    // Strip protocol+host, query string, and _NNNxNNN Shopify size suffix for reliable comparison.
    // e.g. "https://cdn.shopify.com/s/files/image_600x.jpg?v=1" → "/s/files/image.jpg"
    _normSrc(src) {
      if (!src) return '';
      return src
        .replace(/^https?:\/\/[^/]+/, '')
        .split('?')[0]
        .replace(/_\d+x\d*\./, '.');
    }

    // Walk every [data-variant-ids] item in the gallery and modal and write the
    // comma-separated list of variant IDs whose custom.gallery includes that image.
    _tagItems() {
      if (!this.mediaIndex || !this.variantData) return;

      const srcToDomId = new Map(this.mediaIndex.map((m) => [this._normSrc(m.src), m.id]));

      const galleryItems = this.querySelectorAll('[data-variant-ids]');
      const modalItems = document.querySelectorAll(`#ProductModal-${this.sectionId} [data-variant-ids]`);
      [...galleryItems, ...modalItems].forEach((el) => (el.dataset.variantIds = ''));

      for (const [variantId, files] of Object.entries(this.variantData)) {
        for (const f of files) {
          const key = this._normSrc(f.src);
          const domId = srcToDomId.get(key);

          let targets;
          if (domId) {
            targets = [
              ...this.querySelectorAll(`[data-media-id="${domId}"][data-variant-ids]`),
              ...document.querySelectorAll(`#ProductModal-${this.sectionId} [data-media-id="${domId}"][data-variant-ids]`),
            ];
          } else {
            // File is not in product.media — match by the data-src on extra <li>/<div> elements
            targets = [
              ...this.querySelectorAll('[data-extra-file][data-variant-ids]'),
              ...document.querySelectorAll(`#ProductModal-${this.sectionId} [data-extra-file][data-variant-ids]`),
            ].filter((el) => this._normSrc(el.dataset.src) === key);
          }

          targets.forEach((el) => {
            const cur = el.dataset.variantIds ? el.dataset.variantIds.split(',') : [];
            if (!cur.includes(variantId)) {
              cur.push(variantId);
              el.dataset.variantIds = cur.join(',');
            }
          });
        }
      }
    }

    // Show/hide gallery and modal items based on the selected variant.
    // Falls back to showing everything when the variant has no custom.gallery set.
    _applyFilter(variantId, featuredMediaId) {
      const hasGallery = Boolean(this.variantData[variantId]);

      this.querySelectorAll('[data-variant-ids]').forEach((el) => {
        el.hidden = this._shouldHide(el, variantId, hasGallery);
      });

      document.querySelectorAll(`#ProductModal-${this.sectionId} [data-variant-ids]`).forEach((el) => {
        el.hidden = this._shouldHide(el, variantId, hasGallery);
      });

      // Mirror hidden state onto thumbnail rail items by their data-target attribute
      this.querySelectorAll('[data-target]').forEach((thumb) => {
        const target = this.querySelector(`[data-media-id="${thumb.dataset.target}"]`);
        if (target) thumb.hidden = target.hidden;
      });

      // Set the active slide: prefer featured_media if it is in the visible set
      const mediaGallery = this.querySelector('media-gallery');
      const featuredEl =
        featuredMediaId &&
        this.querySelector(`[data-media-id="${this.sectionId}-${featuredMediaId}"]:not([hidden])`);
      const firstVisible = this.querySelector('media-gallery [data-media-id]:not([hidden])');
      const activeEl = featuredEl || firstVisible;
      if (activeEl) {
        mediaGallery?.setActiveMedia?.(activeEl.dataset.mediaId, false);
      }
    }

    _shouldHide(el, variantId, hasGallery) {
      if (!hasGallery) return false;
      const ids = el.dataset.variantIds ? el.dataset.variantIds.split(',') : [];
      // Hide when the item belongs to any variant gallery but not this one,
      // or when it is unassigned (shared) and the current variant has its own gallery.
      return !ids.includes(variantId);
    }
  }

  customElements.define('variant-gallery', VariantGallery);
}
